import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts'
import { CHART_COLORS } from '@/lib/chart-theme'
import { StatCard, fmt$, ChartTooltip, InfoTooltip } from './shared'

export function AvgDealSizeReport({ data }: { data: any }) {
  const summary = data?.summary || {}
  const chartData = data?.chartData || {}
  const monthly = (chartData.monthly || []).map((m: any) => ({
    ...m,
    high: m.high ?? m.avgDealSize ?? 0,
    low: m.low ?? m.avgDealSize ?? 0,
  }))
  return (
    <div className="rep-report">
      <div className="rep-stats-row">
        <StatCard title="Average Deal Size" value={fmt$(summary.averageDealSize)} color={CHART_COLORS.blue} />
        <StatCard title="Largest Deal" value={fmt$(summary.largestDeal ?? 0)} color={CHART_COLORS.purple} />
        <StatCard title="Smallest Deal" value={fmt$(summary.smallestDeal ?? 0)} color={CHART_COLORS.coral} />
        <StatCard title="Total Deals" value={summary.totalDeals} />
      </div>

      <div className="rep-charts-row">
        <div className="rep-chart-card rep-chart-full">
          <InfoTooltip text="An area chart with a high/low range band shows deal size trends while revealing volatility — the shaded band makes it easy to see if your deals are getting more or less consistent over time." />
          <ResponsiveContainer width="100%" height={340}>
            <AreaChart data={monthly} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gradRange" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.blue} stopOpacity={0.1} />
                  <stop offset="100%" stopColor={CHART_COLORS.blue} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gradAvg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.blue} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={CHART_COLORS.blue} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94A3B8' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94A3B8' }}
                tickFormatter={(v: number) => v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={summary.averageDealSize} stroke={CHART_COLORS.coral} strokeDasharray="6 4" strokeWidth={1.5}
                label={{ value: `Avg ${fmt$(summary.averageDealSize)}`, fill: CHART_COLORS.coral, fontSize: 11, position: 'right' }} />

              {/* High/low range band — high filled, low masks the bottom */}
              <Area type="monotone" dataKey="high" stroke="none" fill="url(#gradRange)" name="High" />
              <Area type="monotone" dataKey="low" stroke={CHART_COLORS.blue} strokeDasharray="4 4"
                strokeOpacity={0.25} fill="#fff" name="Low" />

              {/* Average line */}
              <Area type="monotone" dataKey="avgDealSize" name="Avg Deal Size" stroke={CHART_COLORS.blue}
                strokeWidth={2.5} fill="url(#gradAvg)" dot={{ r: 4, fill: CHART_COLORS.blue, stroke: '#fff', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="rep-area-legend">
            <span className="rep-area-legend-item">
              <span className="rep-area-legend-dot" style={{ background: CHART_COLORS.blue }} /> Average
            </span>
            <span className="rep-area-legend-item">
              <span className="rep-area-legend-dot" style={{ background: CHART_COLORS.blue, opacity: 0.2 }} /> High/Low Range
            </span>
          </div>
        </div>
      </div>

      <div className="rep-table-card">
        <h3 className="rep-chart-title">Monthly Breakdown</h3>
        <table className="rep-table">
          <thead><tr><th>Month</th><th>Avg Deal Size</th><th>Highest</th><th>Lowest</th><th>Total Value</th><th>Deals</th></tr></thead>
          <tbody>
            {monthly.map((r: any, i: number) => (
              <tr key={i}>
                <td>{r.month}</td>
                <td>{fmt$(r.avgDealSize)}</td>
                <td>{fmt$(r.high)}</td>
                <td>{fmt$(r.low)}</td>
                <td>{fmt$(r.totalValue)}</td>
                <td>{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
